import React, { useEffect, useState } from "react";
import styles from "./RefundCalculation.module.scss";
import classnames from "classnames";
import { getFilteredRefundOptions, getProductVariantById, isAnyComplaintSelected, isAnyResaleSelected, productToSelectedProductValues } from "~/cores/appCore";
import { formatCurrency } from "../../utils";
import { ServiceErrorAlert } from "../alerts/Error";
import { COLORS, REFUND_OPTIONS } from "~/constants";
import { default as ReactCollapsibleDefault } from "react-collapsible";
import { isResaleActive } from "~/cores/shipment.core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import SkeletonLoaderCard from "../skeletons/SkeletonLoaderCard";
import SkeletonLoaderCardSlim from "../skeletons/SkeletonLoaderCardSlim";
import { serviceIsFetched, serviceIsLoading, serviceIsSuccessfullyFetched } from "rylak-common";
import { isBrandFrameBackgroundSolidWhite } from "~/cores/brandDataCore";

export default function RefundCalculation(props) {
    const {
        appProps,
        refundCalculateServiceData,
        getLocaleText,
        receiptHeader,
        loginServiceData,
        selectedProductsValues,
        refundOption,
        totalPriceHeader,
        totalPriceSubHeader,
        collapsible = false,
    } = props;

    const [latestServiceData, setLatestServiceData] = useState(refundCalculateServiceData);

    useEffect(() => {
        if (serviceIsFetched(refundCalculateServiceData)) {
            setLatestServiceData(refundCalculateServiceData);
        }
        if (serviceIsLoading(refundCalculateServiceData) && selectedProductsValues?.length) {
            setHidden(false);
        }
    }, [refundCalculateServiceData]);

    const refund = latestServiceData.data?.refund;
    const [expanded, setExpanded] = useState(!collapsible);
    const [hidden, setHidden] = useState(!!selectedProductsValues?.length);

    const complaintExists = isAnyComplaintSelected(appProps);
    const resaleExists = isAnyResaleSelected(appProps);
    const currencyCode = refund?.currencyCode;
    const isGiftCardSelected = refundOption?.type === REFUND_OPTIONS.GIFT_CARD;

    useEffect(() => {
        if (hidden && collapsible) {
            setExpanded(false);
        }
    }, [hidden]);

    let refundLines = refund?.lines || [];
    if (getFilteredRefundOptions(props)) {
        let refundOptionSelectionLine = { type: "INVISIBLE", value: null }; // Special option to avoid UX jumping
        if (refundOption && refundOption.type === REFUND_OPTIONS.FAST && refundOption.price) {
            refundOptionSelectionLine = {
                type: "FAST_REFUND",
                value: -refundOption.price,
            };
        }
        if (refundOption && refundOption.type === REFUND_OPTIONS.GIFT_CARD) {
            refundOptionSelectionLine = {
                type: "GIFT_CARD_ADDITION",
                value: refund?.totalAmount * latestServiceData?.data?.giftCardBonus,
            };
        }
        refundLines = refundLines.concat(refundOptionSelectionLine);
    }
    const totalRefund = refundLines.reduce((result, line) => result + line.value, 0);

    useEffect(() => {
        if (selectedProductsValues.length === 0) {
            setHidden(true);
        }
    }, [selectedProductsValues]);

    return (
        <section
            className={styles["receipt-container"]}
            style={{
                visibility: hidden && "hidden",
                position: "relative",
                minHeight: !collapsible && "170px",
            }}
        >
            <ServiceErrorAlert serviceData={latestServiceData} getLocaleText={getLocaleText} />
            {serviceIsLoading(refundCalculateServiceData) && (collapsible ? <SkeletonLoaderCardSlim /> : <SkeletonLoaderCard />)}
            {serviceIsSuccessfullyFetched(latestServiceData) && (
                <div
                    onClick={() => {
                        collapsible && setExpanded(!expanded);
                    }}
                    style={{
                        visibility: serviceIsLoading(refundCalculateServiceData) && "hidden",
                    }}
                    className={classnames(
                        styles["receipts-wrapper"],
                        (collapsible || !isBrandFrameBackgroundSolidWhite(appProps)) && styles["receipts-wrapper-container"],
                        collapsible && styles["receipts-wrapper-collapsible"],
                    )}
                >
                    <ReactCollapsibleDefault open={expanded} transitionTime={200}>
                        {!collapsible && <h2 className={styles["receipt-header"]}>{receiptHeader || getLocaleText(isResaleActive(props) ? "receipt-header-resale" : "receipt-header")}</h2>}
                        <div className={classnames(styles["receipts"])}>
                            {latestServiceData?.data &&
                                refundLines.map(({ productVariantId, value, type }, index) => {
                                    const product = productVariantId && getProductVariantById({ loginServiceData }, productVariantId);
                                    const selectedProductValues = product && productToSelectedProductValues(appProps, product);
                                    const swapProduct = selectedProductValues?.swapProduct;
                                    const giftCardBonus = latestServiceData.data?.giftCardBonus;

                                    return (
                                        <div key={index} className={styles["receipt-wrapper"]}>
                                            <div className={classnames(styles["receipt-name"], "potentially-black")}>
                                                {type === "ITEM_REFUND" && product?.productName + (swapProduct ? ` > ${swapProduct?.productName}` : "")}
                                                {type === "ITEM_RESALE" && (
                                                    <span>
                                                        {product?.productName}{" "}
                                                        <span
                                                            style={{
                                                                fontSize: "12px",
                                                            }}
                                                        >
                                                            ({getLocaleText("RESALE")})
                                                        </span>
                                                    </span>
                                                )}
                                                {type !== "ITEM_REFUND" &&
                                                    type !== "ITEM_RESALE" &&
                                                    getLocaleText(["confirmation-page.refund", type === "GIFT_CARD_ADDITION" && !giftCardBonus ? "GIFT_CARD_ADDITION_NO_BONUS" : type], {
                                                        bonusPercentage: giftCardBonus * 100,
                                                    })}
                                            </div>
                                            <div className={styles["receipt-value"]}>
                                                {formatCurrency({
                                                    amount: value,
                                                    currency: currencyCode,
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                        {refundLines.length > 0 && <div className={classnames(styles["divider"])} />}
                        {complaintExists && <div className={styles["complaint-warning"]}>{getLocaleText("confirmation-page.total-amount.complaint-warning")}</div>}
                        {resaleExists && <div className={styles["complaint-warning"]}>{getLocaleText("confirmation-page.total-amount.resale-info")}</div>}
                    </ReactCollapsibleDefault>
                    <div className={styles["receipt-wrapper"]}>
                        <div className={styles["total-price-header"]}>
                            {(complaintExists || resaleExists) && <span>*&nbsp;</span>}
                            {totalPriceHeader}
                            {isGiftCardSelected && (
                                <span className={classnames(styles["total-price-gift-card"], "potentially-black")}>
                                    &nbsp;(
                                    {getLocaleText("confirmation-page-total-amount.gift-card")})
                                </span>
                            )}
                            {collapsible && <FontAwesomeIcon className={classnames(styles["dropdown-icon"], expanded && styles["dropdown-icon-collapsed"])} icon={faChevronRight} />}
                        </div>

                        <div className={styles["total-price"]}>
                            {latestServiceData?.data &&
                                formatCurrency({
                                    amount: totalRefund,
                                    currency: currencyCode,
                                })}
                        </div>
                    </div>
                    {totalPriceSubHeader && (
                        <div
                            className={styles["total-price-sub-header"]}
                            style={{
                                fontSize: "12px",
                                color: COLORS.textGray,
                                marginTop: "-4px",
                            }}
                        >
                            {totalPriceSubHeader}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
